export const QUICK_START_ROUTE = '/agenda/quick-start'

export const QUICK_START_STAGE = Object.freeze({
  DURATION: 'duration',
  TITLE: 'title',
  AGENDA_ITEM: 'agenda-item',
  ADD_ANOTHER: 'add-another',
  CREATING: 'creating',
})

export const QUICK_START_PROGRESS_STEPS = ['Time', 'Title', 'Agenda', 'Create']
export const QUICK_START_DEFAULT_TITLE = 'Rithmo Meeting'

/** Matches MeetingBuilderPage agenda title validation copy. */
export const QUICK_START_AGENDA_EMPTY_ERROR = 'Add at least one agenda item before continuing.'
export const QUICK_START_AGENDA_TITLE_ERROR = 'Every agenda item needs a title.'

export function getQuickStartAgendaItemValidationError(rawTitle, agendaItemIndex) {
  const title = String(rawTitle ?? '').trim()
  if (title) return null
  return agendaItemIndex === 0 ? QUICK_START_AGENDA_EMPTY_ERROR : QUICK_START_AGENDA_TITLE_ERROR
}

export const QUICK_START_DEFAULT_GOVERNANCE_CONFIG = Object.freeze({
  talkEquity: { enabled: true },
  interruptionPrevention: { enabled: true },
  agendaEnforcement: { enabled: true },
  autoEndMeeting: false,
  demoMode: false,
  requireOutputOwnership: false,
})

export function getQuickStartOrganizerTimezone(user) {
  const fromUser = user?.timezone?.trim?.()
  if (fromUser) return fromUser
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch {
    return ''
  }
}

export function createInitialQuickStartFlowState() {
  return {
    stage: QUICK_START_STAGE.DURATION,
    history: [],
    durationMinutes: null,
    title: '',
    agendaItems: [''],
    agendaItemIndex: 0,
    error: null,
  }
}

function pushHistory(state) {
  return [
    ...state.history,
    {
      stage: state.stage,
      agendaItemIndex: state.agendaItemIndex,
    },
  ]
}

export function quickStartFlowReducer(state, action) {
  switch (action.type) {
    case 'select-duration':
      return {
        ...state,
        durationMinutes: action.durationMinutes,
        stage: QUICK_START_STAGE.TITLE,
        history: pushHistory(state),
        error: null,
      }
    case 'update-title':
      return {
        ...state,
        title: action.value,
        error: state.stage === QUICK_START_STAGE.TITLE ? null : state.error,
      }
    case 'continue-title':
      return {
        ...state,
        stage: QUICK_START_STAGE.AGENDA_ITEM,
        agendaItemIndex: 0,
        history: pushHistory(state),
        error: null,
      }
    case 'update-agenda-item': {
      const agendaItems = [...state.agendaItems]
      agendaItems[state.agendaItemIndex] = action.value
      return {
        ...state,
        agendaItems,
        error: null,
      }
    }
    case 'continue-agenda-item': {
      const rawTitle = state.agendaItems[state.agendaItemIndex] ?? ''
      const title = String(rawTitle).trim()
      if (!title) {
        return state
      }
      const agendaItems = [...state.agendaItems]
      agendaItems[state.agendaItemIndex] = title
      return {
        ...state,
        agendaItems,
        stage: QUICK_START_STAGE.ADD_ANOTHER,
        history: pushHistory(state),
        error: null,
      }
    }
    case 'choose-add-another':
      if (action.addAnother) {
        return {
          ...state,
          agendaItems: [...state.agendaItems, ''],
          agendaItemIndex: state.agendaItems.length,
          stage: QUICK_START_STAGE.AGENDA_ITEM,
          history: pushHistory(state),
          error: null,
        }
      }
      return {
        ...state,
        stage: QUICK_START_STAGE.CREATING,
        error: null,
      }
    case 'finish-adding-agenda-items': {
      if (state.stage !== QUICK_START_STAGE.AGENDA_ITEM || state.agendaItemIndex === 0) {
        return state
      }
      if (state.history.length === 0) return state
      return {
        ...state,
        agendaItems: state.agendaItems.slice(0, state.agendaItemIndex),
        agendaItemIndex: 0,
        stage: QUICK_START_STAGE.ADD_ANOTHER,
        history: state.history.slice(0, -1),
        error: null,
      }
    }
    case 'back': {
      if (state.history.length === 0) return state
      const previous = state.history[state.history.length - 1]
      let agendaItems = state.agendaItems
      if (
        state.stage === QUICK_START_STAGE.AGENDA_ITEM &&
        state.agendaItemIndex > 0 &&
        !String(state.agendaItems[state.agendaItemIndex] ?? '').trim()
      ) {
        agendaItems = state.agendaItems.slice(0, state.agendaItemIndex)
      }
      return {
        ...state,
        stage: previous.stage,
        agendaItemIndex: previous.agendaItemIndex,
        agendaItems,
        history: state.history.slice(0, -1),
        error: null,
      }
    }
    case 'create-failed':
      return {
        ...state,
        stage: QUICK_START_STAGE.ADD_ANOTHER,
        error: action.message || 'Failed to create the meeting. Please try again.',
      }
    default:
      return state
  }
}

export function getQuickStartProgressIndex(stage) {
  if (stage === QUICK_START_STAGE.DURATION) return 0
  if (stage === QUICK_START_STAGE.TITLE) return 1
  if (stage === QUICK_START_STAGE.AGENDA_ITEM) return 2
  return 3
}

export function buildQuickStartAgendaItems(agendaItems, durationMinutes) {
  const cleanTitles = (agendaItems ?? [])
    .map((item) => String(item ?? '').trim())
    .filter(Boolean)

  if (cleanTitles.length === 0) {
    throw new Error('At least one agenda item is required')
  }

  const totalSeconds = Math.max(60, Math.round(Number(durationMinutes) * 60))
  const baseSeconds = Math.floor(totalSeconds / cleanTitles.length)
  let remainingSeconds = totalSeconds - (baseSeconds * cleanTitles.length)

  return cleanTitles.map((title, index) => {
    const extraSecond = remainingSeconds > 0 ? 1 : 0
    remainingSeconds = Math.max(0, remainingSeconds - 1)
    return {
      id: `quick_start_item_${index + 1}`,
      title,
      durationSec: baseSeconds + extraSecond,
      order: index,
    }
  })
}

export function buildQuickStartCreatePayload({
  durationMinutes,
  title,
  agendaItems,
  organizerTimezone,
  now = new Date(),
}) {
  const start = now instanceof Date ? new Date(now.getTime()) : new Date(now)
  if (Number.isNaN(start.getTime())) {
    throw new Error('Quick Start could not determine a valid meeting start time')
  }

  const normalizedDuration = Number(durationMinutes)
  if (!Number.isFinite(normalizedDuration) || normalizedDuration <= 0) {
    throw new Error('Quick Start requires a valid meeting duration')
  }

  const end = new Date(start.getTime() + normalizedDuration * 60 * 1000)
  const normalizedTitle = String(title ?? '').trim() || QUICK_START_DEFAULT_TITLE

  return {
    title: normalizedTitle,
    meetingType: 'default',
    agenda: buildQuickStartAgendaItems(agendaItems, normalizedDuration),
    participants: [],
    governanceConfig: QUICK_START_DEFAULT_GOVERNANCE_CONFIG,
    audienceMode: 'internal',
    isDraft: false,
    createWithZoom: true,
    enableRecallBot: true,
    recallTranscriptionOnly: true,
    scheduledStartTime: start.toISOString(),
    scheduledEndTime: end.toISOString(),
    isRecurring: false,
    organizerTimezone: organizerTimezone || undefined,
    creationSource: 'quick_start',
  }
}

export function normalizeQuickStartMeetingResponse(response) {
  const raw = response?.data ?? response ?? {}
  const meetingData = raw.meeting ?? raw
  const joinUrl =
    meetingData?.joinUrl ??
    meetingData?.join_url ??
    meetingData?.meeting_url ??
    raw.joinUrl ??
    raw.join_url ??
    ''

  const meetingId = String(
    meetingData?.meetingId ??
    meetingData?.meetingNumber ??
    meetingData?.id ??
    raw.meetingId ??
    raw.meetingNumber ??
    ''
  )

  if (!meetingId) {
    throw new Error('Quick Start succeeded but no meeting ID was returned')
  }

  if (!joinUrl) {
    throw new Error('Quick Start succeeded but no Zoom join URL was returned')
  }

  const cppBot = raw.cppBot
  const createdWithUserZoom = raw.createdWith === 'user_oauth'
  const addBotAfterYouJoin =
    cppBot != null && typeof cppBot === 'object'
      ? cppBot.addBotAfterYouJoin === true
      : false

  return {
    meetingId,
    joinUrl: String(joinUrl),
    password:
      meetingData?.password != null && meetingData.password !== ''
        ? String(meetingData.password)
        : null,
    createdWithUserZoom: Boolean(createdWithUserZoom),
    cppBotJoined:
      cppBot != null && typeof cppBot === 'object'
        ? cppBot.joined === true
        : false,
    addBotAfterYouJoin: Boolean(addBotAfterYouJoin) || createdWithUserZoom,
  }
}

export async function createQuickStartMeeting({
  meetingsApi,
  durationMinutes,
  title,
  agendaItems,
  organizerTimezone,
  now = new Date(),
}) {
  if (!meetingsApi || typeof meetingsApi.create !== 'function') {
    throw new Error('Quick Start requires meetingsAPI.create')
  }

  const payload = buildQuickStartCreatePayload({
    durationMinutes,
    title,
    agendaItems,
    organizerTimezone,
    now,
  })

  const response = await meetingsApi.create(payload)
  return normalizeQuickStartMeetingResponse(response)
}
